import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, Clock, Download, Gift } from 'lucide-react';
import html2canvas from 'html2canvas';
import { queryKeys } from '@/api/queryKeys';
import { get } from '@/api/client';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { comma, dateTime, dateOnly } from '@/lib/format';
import { toast } from '@/store/toastStore';

interface ExchangeRow {
  id: number;
  item_code: string;
  item_name: string;
  item_type?: string;
  point_amount: number;
  status: string;
  status_reason: string | null;
  store_name: string | null;
  created_at: string;
  processed_at: string | null;
  viewed_at: string | null;
  expires_at: string | null;
}

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'primary' | 'rise' | 'fall' }> = {
  pending: { label: '대기중', variant: 'default' },
  auto_issued: { label: '자동발급', variant: 'primary' },
  hold: { label: '보류', variant: 'default' },
  approved: { label: '승인', variant: 'rise' },
  rejected: { label: '거절', variant: 'fall' },
  refunded: { label: '환불', variant: 'default' },
  expired: { label: '수령 기간 만료 (환불 불가)', variant: 'fall' },
  failed: { label: '실패', variant: 'fall' },
};

/** 만료까지 남은 시간 — "n일 m시간" 또는 "m시간 후 만료" */
function remainingLabel(expiresAtIso: string | null): string {
  if (!expiresAtIso) return '';
  const ms = new Date(expiresAtIso).getTime() - Date.now();
  if (ms <= 0) return '만료됨';
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}일 ${hours % 24}시간 남음`;
  if (hours >= 1) return `${hours}시간 남음`;
  const mins = Math.max(1, Math.floor(ms / 60_000));
  return `${mins}분 남음`;
}

export function ExchangeHistoryPage() {
  const queryClient = useQueryClient();
  /** id → 노출 중인 외부번호 캐시 (reveal 성공 시 채워짐) */
  const [revealed, setRevealed] = useState<Record<number, string>>({});
  /** id → 복사 완료 표시 (1.5초 후 자동 해제) */
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [errorById, setErrorById] = useState<Record<number, string>>({});
  /** id → 이미지 캡처용 카드 ref (PNG 저장 시 사용) */
  const couponRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const setCouponRef = (id: number) => (el: HTMLDivElement | null) => {
    if (el) couponRefs.current.set(id, el);
    else couponRefs.current.delete(id);
  };
  const [savingImageId, setSavingImageId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.exchange.history,
    queryFn: () => get<{ list: ExchangeRow[] }>('/exchange/history'),
  });

  const revealMutation = useMutation({
    mutationFn: (id: number) =>
      get<{ externalId: string; itemName: string; viewedAt: string; expiresAt: string | null }>(
        `/exchange/${id}/reveal`,
      ),
    onSuccess: (res, id) => {
      setRevealed((r) => ({ ...r, [id]: res.externalId }));
      setErrorById((m) => ({ ...m, [id]: '' }));
      queryClient.invalidateQueries({ queryKey: queryKeys.exchange.history });
    },
    onError: (err: { message?: string }, id) => {
      setErrorById((m) => ({ ...m, [id]: err?.message ?? '번호 확인에 실패했어요.' }));
    },
  });

  const copyToClipboard = async (id: number, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(null), 1500);
    } catch {
      setErrorById((m) => ({ ...m, [id]: '복사에 실패했어요. 직접 선택해 복사해주세요.' }));
    }
  };

  /** Blob → 파일 다운로드 (blob URL + a[download]). 데스크톱/안드로이드 크롬용. */
  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  /** 캡처 실패 시 텍스트 폴백용 — 쿠폰 핵심 정보를 평문으로 구성 */
  const buildCouponText = (r: ExchangeRow, pin: string): string => {
    const lines = [
      '[시세공간 쿠폰]',
      `상품: ${r.item_name}`,
      r.store_name ? `편의점: ${r.store_name}` : '',
      `PIN: ${pin}`,
      `발급일: ${r.processed_at ? dateOnly(r.processed_at) : '-'}`,
      `유효기간: ${r.expires_at ? dateOnly(r.expires_at) : '발급일로부터 약 29~30일'}`,
      '유효기간 내 미사용 시 소멸 · 환불 불가',
    ];
    return lines.filter(Boolean).join('\n');
  };

  /**
   * 쿠폰 카드를 PNG 이미지로 다운로드.
   *  - html2canvas 로 ref 가 가리키는 영역을 캡처 (흰 배경, scale=2)
   *  - 쿠폰 카드는 색상을 모두 인라인 hex/rgba 로 지정해 color-mix(in oklab)/oklch 가
   *    포함되지 않으므로 별도 색상 정규화(onclone) 없이도 캡처 가능.
   *  - 그래도 실패하면 PIN/상품 정보를 클립보드에 텍스트로 복사하는 폴백 제공.
   */
  const saveAsImage = async (r: ExchangeRow, pin: string) => {
    const el = couponRefs.current.get(r.id);
    if (!el) {
      toast.error('쿠폰 영역을 찾지 못했어요.');
      return;
    }
    try {
      setSavingImageId(r.id);
      const canvas = await html2canvas(el, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      const safeName = r.item_name.replace(/[\\/:*?"<>|]/g, '_').slice(0, 40);
      const filename = `시세공간_쿠폰_${safeName}_${r.id}.png`;

      // canvas → Blob (toDataURL 보다 메모리 효율적이고 모바일 공유에 적합)
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );
      if (!blob) throw new Error('canvas toBlob 실패');

      /* 모바일(coarse pointer): iOS Safari 등은 <a download>/data URL 다운로드가 막혀 있어
         Web Share API 로 사진앱 저장/공유를 유도. 데스크톱은 기존처럼 파일 다운로드. */
      const isMobile =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(pointer: coarse)').matches;
      const file = new File([blob], filename, { type: 'image/png' });
      const canShareFile =
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });

      if (isMobile && canShareFile) {
        try {
          await navigator.share({ files: [file], title: '시세공간 기프티콘' });
          toast.success('이미지를 저장/공유했어요.');
        } catch (shareErr: any) {
          // 사용자가 공유 시트를 닫은 경우(AbortError)는 조용히 무시
          if (shareErr?.name !== 'AbortError') {
            // 공유 실패 시 다운로드로 폴백
            triggerBlobDownload(blob, filename);
            toast.success('이미지로 저장됐어요.');
          }
        }
      } else {
        triggerBlobDownload(blob, filename);
        toast.success('이미지로 저장됐어요.');
      }
    } catch (err: any) {
      // 구체적 실패 원인 로깅 + 텍스트 복사 폴백
      const reason = err?.message ? `(${String(err.message).slice(0, 80)})` : '';
      try {
        await navigator.clipboard.writeText(buildCouponText(r, pin));
        toast.error(
          `이미지 저장에 실패해 쿠폰 정보를 텍스트로 복사했어요. 메모장 등에 붙여넣어 보관하세요. ${reason}`,
        );
      } catch {
        toast.error(
          `이미지 저장에 실패했어요. PIN을 직접 복사해 보관해주세요. ${reason}`,
        );
      }
    } finally {
      setSavingImageId(null);
    }
  };

  return (
    <div className="container-base py-10">
      <h1 className="mb-6 text-2xl font-extrabold">교환 내역</h1>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      <div className="space-y-3">
        {data?.list?.map((r) => {
          const status = STATUS_LABELS[r.status] ?? STATUS_LABELS.pending;
          const isApproved = r.status === 'approved';
          const isRevealed = !!r.viewed_at;
          const externalId = revealed[r.id]; // reveal 성공 시 채워짐
          const remaining = remainingLabel(r.expires_at);

          return (
            <div
              key={r.id}
              className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{r.item_name}</div>
                  <div className="mt-1 text-xs text-[var(--color-text-sub)]">
                    {dateTime(r.created_at)}
                  </div>
                </div>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>

              <div className="mt-3 text-sm text-[var(--color-primary)]">
                -{comma(r.point_amount)}P
              </div>

              {/* 승인 + 미수령 — [번호 확인하기] 버튼 + 남은 유효기간 + 유효기간 안내 */}
              {isApproved && !isRevealed && !externalId && (
                <div className="mt-3 rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3">
                  <div className="mb-2 flex items-center gap-1.5 text-xs text-[var(--color-primary)]">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="font-medium">{remaining}</span>
                    <span className="text-[var(--color-fall)]">
                      · 기간 내 미확인 시 환불되지 않습니다
                    </span>
                  </div>
                  <div className="mb-2 text-[11px] leading-relaxed text-[var(--color-text-sub)]">
                    쿠폰번호를 29일 이내에 확인하세요.
                    <br />
                    상품권 유효기간: 발급일로부터 약 29~30일
                  </div>
                  <Button
                    size="sm"
                    fullWidth
                    onClick={() => revealMutation.mutate(r.id)}
                    loading={revealMutation.isPending && revealMutation.variables === r.id}
                  >
                    번호 확인하기
                  </Button>
                </div>
              )}

              {/* 승인 + 확인 완료 (또는 방금 reveal) — 번호 노출 + 복사 + 이미지로 저장 */}
              {isApproved && (isRevealed || externalId) && (
                <div className="mt-3">
                  {externalId ? (
                    <>
                      {/* 이미지로 저장될 영역 — 티켓형 쿠폰 디자인.
                          html2canvas 호환을 위해 색상/그라데이션은 모두 인라인 hex/rgba 로 지정
                          (Tailwind v4 opacity 수식어 `/숫자` 는 color-mix(in oklab) 로 컴파일되어 캡처 실패하므로 사용 금지).
                          레이아웃 클래스(padding/rounded/grid/font)만 Tailwind 사용. */}
                      <div
                        ref={setCouponRef(r.id)}
                        className="mx-auto max-w-sm overflow-hidden rounded-2xl"
                        style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                      >
                        {/* 헤더 밴드 */}
                        <div
                          className="flex items-center gap-2 px-5 py-3"
                          style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}
                        >
                          <Gift className="h-5 w-5" style={{ color: '#ffffff' }} />
                          <span
                            className="text-sm font-bold tracking-wide"
                            style={{ color: '#ffffff' }}
                          >
                            시세공간 기프티콘
                          </span>
                        </div>

                        {/* 본문 */}
                        <div className="px-5 py-5">
                          {r.store_name && (
                            <div className="mb-3 flex justify-center">
                              <span
                                className="rounded-full px-3 py-1 text-xs font-semibold"
                                style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}
                              >
                                {r.store_name}
                              </span>
                            </div>
                          )}
                          <div
                            className="text-center text-lg font-extrabold leading-snug"
                            style={{ color: '#111827' }}
                          >
                            {r.item_name}
                          </div>

                          {/* PIN 강조 박스 */}
                          <div
                            className="my-4 rounded-xl px-4 py-4 text-center"
                            style={{ backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1' }}
                          >
                            <div
                              className="mb-1.5 text-[10px] font-semibold tracking-[0.2em]"
                              style={{ color: '#64748b' }}
                            >
                              PIN NUMBER
                            </div>
                            <div
                              className="break-all font-mono text-2xl font-bold tracking-wider"
                              style={{ color: '#0f172a' }}
                            >
                              {externalId}
                            </div>
                          </div>

                          {/* 점선 구분선 */}
                          <div className="my-4" style={{ borderTop: '1px dashed #e5e7eb' }} />

                          {/* 정보 */}
                          <div className="flex flex-col gap-1.5 text-xs">
                            <div className="flex items-center justify-between">
                              <span style={{ color: '#94a3b8' }}>발급일</span>
                              <span className="font-medium" style={{ color: '#334155' }}>
                                {r.processed_at ? dateOnly(r.processed_at) : '-'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span style={{ color: '#94a3b8' }}>유효기간</span>
                              <span className="font-medium" style={{ color: '#334155' }}>
                                {r.expires_at ? dateOnly(r.expires_at) : '발급일로부터 약 29~30일'}
                              </span>
                            </div>
                          </div>

                          <div
                            className="mt-4 rounded-lg px-3 py-2 text-center text-[10px] leading-relaxed"
                            style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}
                          >
                            유효기간 내 미사용 시 소멸 · 환불 불가
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => copyToClipboard(r.id, externalId)}
                        >
                          {copiedId === r.id ? (
                            <>
                              <Check className="h-4 w-4" /> 복사됨
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" /> PIN 복사
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => saveAsImage(r, externalId)}
                          loading={savingImageId === r.id}
                        >
                          <Download className="h-4 w-4" /> 이미지로 저장
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* viewed_at 은 있지만 페이지 진입 후 reveal 안 한 상태 — 다시 클릭 유도 */
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
                      <div className="mb-2 text-[11px] leading-relaxed text-[var(--color-text-sub)]">
                        상품권 유효기간: 발급일로부터 약 29~30일 (유효기간 내 미사용 시 소멸)
                      </div>
                      <Button
                        size="sm"
                        onClick={() => revealMutation.mutate(r.id)}
                        loading={revealMutation.isPending && revealMutation.variables === r.id}
                      >
                        번호 다시 보기
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* 만료 — 수령 기간(29일) 만료, 환불 불가 (2026-05 정책 개편) */}
              {r.status === 'expired' && (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  수령 기간(29일) 만료 — 환불 불가. {comma(r.point_amount)}P 는 환불되지 않습니다.
                </div>
              )}

              {/* 거절/환불 사유 */}
              {(r.status === 'rejected' || r.status === 'refunded') && r.status_reason && (
                <div className="mt-2 text-xs text-[var(--color-text-sub)]">
                  사유: {r.status_reason}
                </div>
              )}

              {errorById[r.id] && (
                <div className="mt-2 text-xs text-[var(--color-fall)]">
                  {errorById[r.id]}
                </div>
              )}
            </div>
          );
        })}
        {data?.list?.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
            아직 교환 내역이 없어요.
          </div>
        )}
      </div>
    </div>
  );
}
